import _intersection from 'lodash/intersection';
import { BaseEntity, Entity as DecribeEntity, Relation } from '@avangard/utils/entity';

import { i18n } from '@lib/i18n';
import { roleTranslates } from '@modules/auth/constants';
import { StudentEntity } from '@modules/students/entities';
import { SchoolEntity } from '@modules/schools/entities';
import { UserRole, UserGrantCategoryToken, UserGrantActionToken } from '@modules/types/graphql';

import type { User } from '@modules/types/graphql';

export type Entity = Partial<User> | null;

@DecribeEntity('UserEntity')
class UserEntity extends BaseEntity {
    id: string;
    username: string;
    email: string;
    fullName: string;
    roles: (UserRole | null)[];
    grants: Record<UserGrantCategoryToken, UserGrantActionToken[]>;

    @Relation(() => StudentEntity)
    student: StudentEntity;

    @Relation(() => SchoolEntity)
    school: SchoolEntity;

    constructor(entity: Entity) {
        super(entity);

        this.id = entity?.id ?? '';
        this.username = entity?.username ?? '';
        this.email = entity?.email ?? '';
        this.fullName = entity?.fullName ?? '';
        this.roles = entity?.roles ?? [];
    }

    getRole() {
        if (this.roles.length === 0) {
            return '-';
        }

        if (this.roles && this.roles[0]) {
            const role = this.roles[0];

            return i18n.t(roleTranslates[role]);
        }

        return this.roles.toString();
    }

    getCurrentRole() {
        const role = this.roles?.[0];

        return role;
    }

    getFullNameByRole() {
        if (this.isStudent()) {
            return this.student.fullName;
        }

        if (this.isSchool()) {
            return this.school.shortName;
        }

        return this.fullName;
    }

    getFullNameWithRole() {
        const role = this.getRole().toLowerCase();
        const fullName = this.getFullNameByRole();

        return `${role} ${fullName}`;
    }

    authenticated() {
        const isAuth = this.id !== '';

        return isAuth;
    }

    hasAdminPrivileges() {
        return this.isSuperAdmin() || this.isAdmin();
    }

    isSuperAdmin() {
        const isSuperAdmin = this.roles.includes(UserRole.superAdmin);

        return isSuperAdmin;
    }

    isAdmin() {
        const isAdmin = this.roles.includes(UserRole.admin);

        return isAdmin;
    }

    isInstructor() {
        const isInstructor = this.roles.includes(UserRole.instructor);

        return isInstructor;
    }

    isLeader() {
        const isLeader = this.roles.includes(UserRole.leader);

        return isLeader;
    }

    isMentor() {
        const isMentor = this.roles.includes(UserRole.mentor);

        return isMentor;
    }

    isSchool() {
        const isSchool = this.roles.includes(UserRole.school);

        return isSchool;
    }

    isMethodist() {
        const isMethodist = this.roles.includes(UserRole.methodist);

        return isMethodist;
    }

    isRegistrar() {
        const isRegistrar = this.roles.includes(UserRole.registrar);

        return isRegistrar;
    }

    isStudent() {
        const isStudent = this.roles.includes(UserRole.student);

        return isStudent;
    }

    hasAccess(roles?: UserRole[]) {
        if (!roles) {
            return true;
        }

        const hasAccess = _intersection(this.roles, roles).length !== 0;

        return hasAccess;
    }
}

export { UserEntity };
